<template>
  <VPInputBox
    :class="{ dirty: meta.dirty, 'has-error': errorMessage }"
  >
    <VPLabel
      v-if="label"
      :id="uid + '-label'"
      :for="uid"
    >
      {{ label }}
    </VPLabel>

    <Accessible v-if="accessibleLabel">
      <span :id="uid + '-accessible-label'">
        {{ accessibleLabel }}
      </span>
    </Accessible>

    <div
      class="flex h-10 items-center rounded-md my-2 py-1.5 px-2 transition-all cursor-text ring-1 ring-inset"
      :class="[wrapperClasses]"
    >
      <span
        v-if="$slots.prefix"
        class="pr-2"
      >
        <slot name="prefix" />
      </span>
      <input
        v-bind="{ ...$attrs, ...inputAttrs }"
        :id="`${uid}`"
        ref="inputRef"
        :value="displayValue ?? value"
        :disabled="disabled"
        class="px-0 w-full bg-transparent text-on-input placeholder:text-text-muted disabled:placeholder:text-content-muted disabled:placeholder:text-neutral-400"
        :class="[inputClasses, { 'no-spinner': props.type === 'number' }]"
        :type="props.type"
        :aria-label="!label ? accessibleLabel : undefined"
        :aria-labelledby="label ? uid + '-label' : undefined"
        :aria-describedby="computedAriaDescribedBy"
        :aria-invalid="!!errorMessage"
        @input="e => onChange(e.target.value)"
      >
      <slot name="suffix">
        <span
          v-if="suffix"
          class="pl-2"
        >
          {{ suffix }}
        </span>
      </slot>
    </div>
    <span
      v-if="hint || $slots.hint"
      :id="uid + '-hint'"
      class="text-text-muted text-xs"
    >
      <slot name="hint">{{ hint }}</slot>
    </span>
    <span
      v-if="errorMessage"
      :id="uid + '-error'"
      class="text-error font-normal text-xs"
      role="alert"
      aria-live="assertive"
    >
      {{ errorMessage }}
    </span>
  </VPInputBox>
</template>

<script setup lang="ts">
import Accessible from '../Accessible.vue';
import VPLabel from './VPLabel.vue';
import { computed, ref, useSlots } from 'vue';
import { useField } from 'vee-validate';
import VPInputBox from './VPInputBox.vue';
import { useInputWrapperClasses } from '../../composables/useInputWrapperClasses';

type Props = {
  id?: string;
  name: string;
  label?: string;
  accessibleLabel?: string;
  suffix?: string;
  disabled?: boolean;
  type?: string;
  hint?: string;
  displayValue?: string;
  variant?: 'title' | 'default' | 'onSurface';
  /**
   * Attributes for the `<input>` and nothing else. This component does not set
   * `inheritAttrs: false` — consumers rely on `class` landing on the wrapper — so a plain
   * attribute reaches the input *and* the wrapping box. That is harmless for `@paste` or
   * `autocomplete`, but not for ARIA: a combobox role applied to both would nest one
   * combobox inside another and announce twice.
   */
  inputAttrs?: Record<string, unknown>;
};

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  disabled: false,
  displayValue: undefined,
  variant: 'default',
  inputAttrs: undefined,
});

const slots = useSlots();
const inputRef = ref<HTMLInputElement>();
const uid = computed(() => props.id ?? `vp-input-${props.name}`);

const {
  value,
  errorMessage,
  meta,
} = useField(() => props.name);

const onChange = v => {
  value.value = v;
};

const focus = () => {
  inputRef.value?.focus();
};

const inputClasses = computed(() => {
  if (props.variant === 'title') {
    return 'text-3xl font-bold text-content caret-vp-primary';
  }
  return 'sm:text-sm';
});

const wrapperClasses = useInputWrapperClasses({
  disabled: computed(() => props.disabled),
  hasError: computed(() => !!errorMessage.value),
  variant: computed(() => props.variant),
});

const computedAriaDescribedBy = computed(() => {
  const ids: string[] = [];

  if (props.hint || slots.hint) {
    ids.push(uid.value + '-hint');
  }

  if (errorMessage.value) {
    ids.push(uid.value + '-error');
  }

  if (props.accessibleLabel) {
    ids.push(uid.value + '-accessible-label');
  }

  return ids.length > 0 ? ids.join(' ') : undefined;
});

defineExpose({
  focus,
});
</script>
